import { ChevronDown, MessageSquare } from "lucide-react";
import React, { useState } from "react";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";

import { useGetSmsTemplates } from "../hooks/queries";
import type { SmsTemplate } from "../types/sms-settings";

interface SelectTemplateButtonProps {
  onTemplateSelect: (template: SmsTemplate) => void;
  selectedTemplateId?: string;
  className?: string;
}

export const SelectTemplateButton = function({
  onTemplateSelect,
  selectedTemplateId,
  className,
}: SelectTemplateButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { data: templatesData, isLoading, error } = useGetSmsTemplates();
  const { toast } = useToast();
  const { user } = useUserContext();

  // handleApiCall extracts the 'data' field, so templatesData is already the pagination object
  const allTemplates = templatesData?.items || [];

  // Check if user is agency owner or admin
  const agencyRole = user?.user?.agencyRole || user?.currentAgency?.role;
  const isAgencyLeader = agencyRole === 'owner' || agencyRole === 'admin';
  const userId = user?.user?.id || user?.user?.userId;
  const agencyId = (user?.currentAgency as any)?.id || user?.user?.agencyId;

  // CRITICAL: Client-side filtering to ensure user only sees templates they should see
  // This is a safety net in case backend filtering doesn't work properly
  const templates = allTemplates.filter((template: SmsTemplate) => {
    // Allow global templates (both null or undefined) - these are system defaults
    // These are the 4 default templates that should be visible to everyone
    const isGlobal = (!template.agencyId || template.agencyId === null) &&
                     (!template.userId || template.userId === null);
    if (isGlobal) {
      return true;
    }

    // Agency-wide templates - show to ALL members of the same agency
    // These are created by owners/admins but visible to all agency members
    if (template.agencyId === agencyId && !template.userId) {
      return true; // Changed from isAgencyLeader to true - all agency members can see
    }

    // Personal templates - only show if userId matches exactly
    if (template.userId === userId && template.agencyId === agencyId) {
      return true;
    }

    // Everything else should be filtered out (other users' templates, other agencies, etc.)
    return false;
  });

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  const handleTemplateSelect = (template: SmsTemplate) => {
    onTemplateSelect(template);
    setIsOpen(false);
    toast({
      title: "Template Selected",
      description: `"${template.name}" template has been selected.`,
    });
  };

  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case "appointment":
        return "bg-blue-100 text-blue-800";
      case "follow-up":
        return "bg-green-100 text-green-800";
      case "quote":
        return "bg-purple-100 text-purple-800";
      case "thank-you":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (error) {
    return (
      <Button disabled className={className} variant="outline">
        <MessageSquare className="h-4 w-4 mr-2" />
        Failed to load templates
      </Button>
    );
  }

  return (
    <DropdownMenu onOpenChange={setIsOpen} open={isOpen}>
      <DropdownMenuTrigger asChild>
        <Button className={className} disabled={isLoading} variant="outline">
          <MessageSquare className="h-4 w-4 mr-2" />
          {isLoading ? (
            "Loading templates..."
          ) : selectedTemplate ? (
            <React.Fragment>
              {selectedTemplate.name}
              <Badge 
                className={`ml-2 ${getCategoryColor(selectedTemplate.category)}`} 
                variant="secondary"
              >
                {selectedTemplate.category}
              </Badge>
            </React.Fragment>
          ) : (
            "Select Template"
          )}
          <ChevronDown className="h-4 w-4 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80 max-h-[400px] overflow-y-auto">
        <DropdownMenuLabel>SMS Templates</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {templates.length === 0 ? (
          <DropdownMenuItem disabled>
            No templates available
          </DropdownMenuItem>
        ) : (
          templates.map((template) => (
            <DropdownMenuItem
              key={template.id}
              className="flex flex-col items-start p-3 cursor-pointer hover:bg-gray-50"
              onClick={() => handleTemplateSelect(template)}
            >
              <div className="flex items-center justify-between w-full mb-2">
                <span className="font-medium">{template.name}</span>
                <Badge 
                  className={getCategoryColor(template.category)} 
                  variant="secondary"
                >
                  {template.category}
                </Badge>
              </div>
              <p className="text-sm text-gray-600 line-clamp-2 w-full">
                {template.content}
              </p>
              <div className="flex items-center justify-between w-full mt-2 text-xs text-gray-500">
                <span>ID: {template.id}</span>
                <span>
                  {new Date(template.updatedAt).toLocaleDateString()}
                </span>
              </div>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
